import React, {
  useRef, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import './typography.scss';
import {
  TypographyColors, MappedInputTags, MappedInputVariants, TypographyTags, TypographyVariants,
  MappedInputFontWeights, TypographyFontWeights, MappedInputColors,
} from './constants';

const AllowedTags = Object.values(MappedInputTags);

const AllowedVariants = Object.values(MappedInputVariants);

const AllowedFontWeights = Object.values(MappedInputFontWeights);

const AllowedColors = Object.values(MappedInputColors);

const Typography = forwardRef(({
  tag: Tag, variant, color, fontWeight, className, children, isStriked, isCapitalized, rows,
}, ref) => {
  const innerRef = useRef();
  const TagElement = TypographyTags[Tag];
  const showEllipsis = rows !== Infinity;

  useImperativeHandle(ref, () => {
    // Show ellipsis by calc max height
    if (showEllipsis) {
      const lineHeight = parseInt(window.getComputedStyle(innerRef.current).lineHeight, 10);
      const maxHeight = lineHeight * rows;
      innerRef.current.style.maxHeight = `${maxHeight}px`;

      // Show three dots at the end of the content
      while (innerRef.current.scrollHeight > maxHeight) {
        innerRef.current.textContent = `${innerRef.current.textContent.slice(0, -4)}...`;
      }
    } else {
      // Reset max height to render full content
      innerRef.current.style.maxHeight = 'unset';
      innerRef.current.textContent = children;
    }

    return innerRef.current;
  }, []);

  return (
    <TagElement
      ref={innerRef}
      className={clsx(
        [
          `typography typography-${TagElement}`,
          { 'typography-striked': isStriked },
          { 'typography-capitalized': isCapitalized },
          { 'text-ellipsis': showEllipsis },
          TypographyFontWeights[fontWeight],
          TypographyVariants[Tag],
          variant && TypographyVariants[variant], TypographyColors[color], className],
      )}
    >
      {children}
    </TagElement>
  );
});

Typography.Tags = MappedInputTags;

Typography.Variants = MappedInputVariants;

Typography.FontWeight = MappedInputFontWeights;

Typography.Colors = MappedInputColors;

Typography.propTypes = {
  tag: PropTypes.oneOf(AllowedTags),
  variant: PropTypes.oneOf([PropTypes.oneOf([null]), ...AllowedVariants]),
  color: PropTypes.oneOf(AllowedColors),
  fontWeight: PropTypes.oneOf(AllowedFontWeights),
  className: PropTypes.string,
  children: PropTypes.string,
  isStriked: PropTypes.bool,
  isCapitalized: PropTypes.bool,
  rows: PropTypes.number,
};

Typography.defaultProps = {
  tag: MappedInputTags.SPAN,
  fontWeight: MappedInputFontWeights.regular,
  variant: null,
  color: AllowedColors.gray900,
  className: '',
  children: '',
  isStriked: false,
  isCapitalized: false,
  rows: Infinity,
};

export default Typography;

export { AllowedTags, AllowedVariants };
